/**
 * What has been imported, in terms a person can recognise.
 *
 * The table used to lead with a database id, a template name and a page
 * counter — "#11 Quality 2 ▓ 1/1" identifies nothing, and none of it answers
 * which file that was, who sent it, or what month it covered. It now leads with
 * the file name and says how many of its rows got in.
 */
import { Button } from '../../../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import { RefreshCw, FileSpreadsheet, Eye, Trash2, AlertCircle, Download } from 'lucide-react';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import { ListPager, ListSearch, useListControls } from '../../../components/common/listControls';
import { SortableTh } from '../../../components/common/SortableTh';
import type { ImportSummary } from '../../../lib/api/barratt';
import ImportStatusBadge from './ImportStatusBadge';

interface ImportHistoryTableProps {
  imports: ImportSummary[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  /** Resolves the template name shown in the Template column. */
  templateNameFor: (imp: ImportSummary) => string;
  onView: (imp: ImportSummary) => void;
  onDelete: (id: number) => void;
}

/** Status as a sortable / exportable word, matching ImportStatusBadge. */
function statusText(imp: ImportSummary): string {
  if (!imp.is_complete) return 'Processing';
  return imp.is_success ? 'Complete' : 'Failed';
}

/** Rows that landed, out of rows read — the question "Records: 2" never answered. */
function rowsImported(imp: ImportSummary): number {
  return imp.total_added ?? (imp.is_success ? imp.total_records : 0);
}

/** 'Aug 2026 · SPEEDY ASSET SERVICES LIMITED' for a monthly file, else ''. */
function coversText(imp: ImportSummary): string {
  return [imp.covers_period_label, imp.supplier_name].filter(Boolean).join(' · ');
}

/** Recent imports: what the file was, what got in, and view / delete actions. */
export default function ImportHistoryTable({
  imports,
  loading,
  error,
  onRetry,
  templateNameFor,
  onView,
  onDelete,
}: ImportHistoryTableProps) {
  const list = useListControls(imports, {
    searchText: (imp) =>
      `${imp.id} ${templateNameFor(imp)} ${imp.file_name ?? ''} ${imp.user_name ?? ''} ${coversText(imp)} ${statusText(imp)}`,
    sorters: {
      file: (imp) => (imp.file_name ?? '').toLowerCase(),
      template: (imp) => templateNameFor(imp).toLowerCase(),
      covers: (imp) => coversText(imp).toLowerCase() || null,
      rows: (imp) => rowsImported(imp),
      user: (imp) => (imp.user_name ?? '').toLowerCase(),
      status: (imp) => statusText(imp),
      date: (imp) => imp.created_at,
    },
    defaultSort: { key: 'date', dir: 'desc' },
    tieBreak: (imp) => String(imp.id),
  });

  const exportExcel = () => {
    const header = ['File', 'Template', 'Covers', 'Rows in file', 'Rows imported', 'Problems', 'Uploaded by', 'Status', 'Date', 'ID'];
    const body = list.orderedRows.map((imp) => [
      imp.file_name ?? '', templateNameFor(imp), coversText(imp),
      imp.total_records, rowsImported(imp), imp.total_failed ?? 0,
      imp.user_name ?? '', statusText(imp),
      format(new Date(imp.created_at), 'yyyy-MM-dd HH:mm'), imp.id,
    ]);
    const sheet = XLSX.utils.aoa_to_sheet([header, ...body]);
    sheet['!cols'] = [{ wch: 40 }, { wch: 22 }, { wch: 34 }, { wch: 12 }, { wch: 13 }, { wch: 13 }, { wch: 22 }, { wch: 12 }, { wch: 18 }, { wch: 8 }];
    const book = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(book, sheet, 'Import history');
    const suffix = list.search.trim() ? `-${list.search.trim().replace(/[^a-z0-9]+/gi, '_').toLowerCase()}` : '';
    XLSX.writeFile(book, `import-history${suffix}.xlsx`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <AlertCircle className="h-10 w-10 text-red-500" />
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  if (imports.length === 0) {
    return (
      <div className="text-center py-12">
        <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No imports yet. Upload a file to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <ListSearch value={list.search} onChange={list.setSearch} placeholder="Search file, template, month, person…" testId="import-search" />
      <Button variant="outline" size="sm" onClick={exportExcel} data-testid="import-export">
        <Download className="h-4 w-4 mr-2" />
        Export Excel
      </Button>
    </div>
    <ListPager list={list} position="top" noun="imports" testIdPrefix="import" />
    <div className="overflow-x-auto">
    <Table>
      <TableHeader>
        <TableRow>
          <SortableTh field="file" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>File</SortableTh>
          <SortableTh field="template" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Template</SortableTh>
          <SortableTh field="covers" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Covers</SortableTh>
          <SortableTh field="rows" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy} align="right">Rows</SortableTh>
          <SortableTh field="user" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Uploaded by</SortableTh>
          <SortableTh field="status" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Status</SortableTh>
          <SortableTh field="date" sortField={list.sortKey} sortDirection={list.sortDir} onSort={list.sortBy}>Date</SortableTh>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {list.pageRows.length === 0 ? (
          <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No imports match "{list.search}".</TableCell></TableRow>
        ) : list.pageRows.map((imp) => {
          const imported = rowsImported(imp);
          const rejected = imp.total_failed ?? 0;
          return (
            <TableRow key={imp.id}>
              <TableCell className="max-w-[20rem]">
                {/* Imports migrated from the legacy portal kept no file name;
                    their id is the only handle anyone has on them. */}
                <p className="truncate font-medium" title={imp.file_name || undefined}>
                  {imp.file_name || `Import #${imp.id}`}
                </p>
                {imp.file_name && <p className="text-xs text-muted-foreground">#{imp.id}</p>}
              </TableCell>
              <TableCell>{templateNameFor(imp)}</TableCell>
              <TableCell className="text-muted-foreground">
                {imp.covers_period_label ? (
                  <>
                    <span className="whitespace-nowrap text-foreground">{imp.covers_period_label}</span>
                    {imp.supplier_name && (
                      <p className="max-w-[16rem] truncate text-xs" title={imp.supplier_name}>{imp.supplier_name}</p>
                    )}
                  </>
                ) : (
                  '—'
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                <span className="font-medium">{imported.toLocaleString()}</span>
                <span className="text-muted-foreground"> of {imp.total_records.toLocaleString()}</span>
                {rejected > 0 && (
                  // Counted from the import logs, which record a failure per bad
                  // row *and* per bad column — so it is problems, not rows, and
                  // says so. The details dialog itemises them.
                  <p className="text-xs text-red-600 dark:text-red-400">
                    {rejected.toLocaleString()} problem{rejected === 1 ? '' : 's'}
                  </p>
                )}
              </TableCell>
              <TableCell className="max-w-[12rem] truncate text-muted-foreground" title={imp.user_name || undefined}>
                {imp.user_name || '—'}
              </TableCell>
              <TableCell>
                <ImportStatusBadge {...imp} />
              </TableCell>
              <TableCell className="whitespace-nowrap text-muted-foreground">
                {format(new Date(imp.created_at), 'd MMM yyyy HH:mm')}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => onView(imp)} aria-label={`View import #${imp.id}`}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDelete(imp.id)}
                    className="text-destructive hover:text-destructive"
                    aria-label={`Delete import #${imp.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
    </div>
    <ListPager list={list} position="bottom" noun="imports" testIdPrefix="import" />
    </div>
  );
}
